import React, { useEffect, useState, useCallback } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
}

const CONFETTI_COLORS = [
  '#FFD700', // Gold
  '#FFC107', // Amber
  '#FF9800', // Orange
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#2196F3', // Blue
  '#00BCD4', // Cyan
  '#4CAF50', // Green
];

/**
 * NewYearConfetti
 * Triggered confetti effect for celebrations
 * Listens for 'seasonal-confetti' custom event
 */
export const NewYearConfetti: React.FC = () => {
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const [isActive, setIsActive] = useState(false);

  const triggerConfetti = useCallback(() => {
    if (isActive) return;

    setIsActive(true);
    const pieces: ConfettiPiece[] = [];

    for (let i = 0; i < 50; i++) {
      pieces.push({
        id: i,
        x: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        size: 6 + Math.random() * 8,
        rotation: Math.random() * 360,
      });
    }

    setConfetti(pieces);

    // Clear after animation
    setTimeout(() => {
      setConfetti([]);
      setIsActive(false);
    }, 4000);
  }, [isActive]);

  // Listen for custom confetti trigger event
  useEffect(() => {
    const handleConfettiEvent = () => triggerConfetti();
    window.addEventListener('seasonal-confetti', handleConfettiEvent);
    return () => window.removeEventListener('seasonal-confetti', handleConfettiEvent);
  }, [triggerConfetti]);

  if (confetti.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${piece.x}%`,
            top: '-20px',
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        >
          <div
            className="animate-confetti-spin"
            style={{
              width: `${piece.size}px`,
              height: `${piece.size * 0.6}px`,
              backgroundColor: piece.color,
              transform: `rotate(${piece.rotation}deg)`,
              borderRadius: '2px',
            }}
          />
        </div>
      ))}

      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 1;
          }
          25% {
            transform: translateY(25vh) translateX(20px);
          }
          50% {
            transform: translateY(50vh) translateX(-15px);
          }
          75% {
            transform: translateY(75vh) translateX(25px);
          }
          100% {
            transform: translateY(100vh) translateX(-10px);
            opacity: 0;
          }
        }

        @keyframes confetti-spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(720deg);
          }
        }

        .animate-confetti-fall {
          animation: confetti-fall linear forwards;
        }

        .animate-confetti-spin {
          animation: confetti-spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default NewYearConfetti;
